/**
 * This facade provides the functionality to determine which charts are in view
 * and have rendered.
 *
 * Not directly related to the facade, but here is the Honeycomb reporting strategy
 * since this seems to be the best place to put general documentation around it.
 *
 * Honeycomb will receive one of two events to mark the completion of a report view.
 * The first type will be if all the charts in view have rendered, and the user has not
 * scrolled. The second is if the user has scrolled and at the moment all the charts in view
 * have rendered. At the time of this writing, the first type will be used
 * for benchmarking, while the second is for future analysis.
 *
 * In order to support this, the basic logic consists of checking if all charts in view have
 * rendered using the selectViewportRendered selector. Then it should proceed to marking an
 * appropriate event and sending the trace. The setScrolled function will determine which event
 * will be sent to honeycomb. This is a pretty straightforward initial implementation, and
 * if we find that scrolling is a very common situation, then this logic will likely need to be
 * refined to gather more insights.
 */
import { Actions, ofType } from '@ngrx/effects';
import { combineLatest, Subject } from 'rxjs';
import { Injectable } from '@angular/core';
import { FeatureFlag, FeatureFlagsFacade } from '@mode/shared/contract-common';
import { Store } from '@ngrx/store';
import { of } from 'rxjs';
import { first, switchMap } from 'rxjs/operators';
import { createVizInViewportSelector } from './virtual-scroll/virtual-scroll.selectors';
import { Observable } from 'rxjs';
import {
  reportBuilderSetVirtualEnabled,
  reportBuilderSetHasRendered,
  reportBuilderSetInViewport,
  reportBuilderSetScrolled,
  reportBuilderVirtualRendered,
  reportBuilderVirtualScrolledRendered,
  reportViewerSetVirtualEnabled,
  reportViewerSetHasRendered,
  reportViewerSetInViewport,
  reportViewerSetScrolled,
  reportViewerVirtualRendered,
  reportViewerVirtualScrolledRendered,
} from './virtual-scroll/virtual-scroll.actions';

@Injectable({
  providedIn: 'root',
})
export class VirtualScrollFacade {
  virtualReportRendered$: Observable<void>;
  virtualReportScrolledRendered$: Observable<void>;
  private virtualReportRenderedNotifier = new Subject<void>();
  private virtualReportScrolledRenderedNotifier = new Subject<void>();

  constructor(private store: Store, actions$: Actions, private featureFlagFacade: FeatureFlagsFacade) {
    actions$.pipe(ofType(reportViewerVirtualScrolledRendered, reportBuilderVirtualScrolledRendered)).subscribe(() => {
      this.virtualReportScrolledRenderedNotifier.next();
    });

    actions$.pipe(ofType(reportViewerVirtualRendered, reportBuilderVirtualRendered)).subscribe(() => {
      this.virtualReportRenderedNotifier.next();
    });

    this.virtualReportRendered$ = this.virtualReportRenderedNotifier;
    this.virtualReportScrolledRendered$ = this.virtualReportScrolledRenderedNotifier;
  }

  viewerSetVirtualEnabled(enabled: boolean) {
    this.store.dispatch(reportViewerSetVirtualEnabled({ enabled }));
  }

  viewerSetInViewport(intersections: Record<string, boolean>) {
    this.store.dispatch(reportViewerSetInViewport({ intersections }));
  }

  viewerSetRendered(token: string, rendered: boolean) {
    this.store.dispatch(reportViewerSetHasRendered({ token, rendered }));
  }

  /**
   * Sets whether the user has scrolled while viewing the report. Not required
   * to be called if the user never scrolls.
   * @param scrolled true if scrolled
   */
  viewerSetScrolled(scrolled: boolean) {
    this.store.dispatch(reportViewerSetScrolled({ scrolled }));
  }

  builderSetInViewport(intersections: Record<string, boolean>) {
    this.store.dispatch(reportBuilderSetInViewport({ intersections }));
  }

  builderSetRendered(token: string, rendered: boolean) {
    this.store.dispatch(reportBuilderSetHasRendered({ token, rendered }));
  }

  builderSetScrolled(scrolled: boolean) {
    this.store.dispatch(reportBuilderSetScrolled({ scrolled }));
  }

  builderSetVirtualEnabled(enabled: boolean) {
    this.store.dispatch(reportBuilderSetVirtualEnabled({ enabled }));
  }

  getInViewportObservable(token: string) {
    /**
     * Note that If the report is opt-ed out, then the embed-layout component must notify that all
     * visualizations are visible to emulate non-virtualized behavior.
     */
    return this.store.select(createVizInViewportSelector(token));
  }
}
